import { AppCommonModule } from './app-common.module';
import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from '@brc/app.component';
import { APP_PROVIDERS } from '@brc/app.providers';
import { environment } from '@environments/environment';
import {
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  enableProdMode,
  importProvidersFrom,
} from '@angular/core';
import { provideHttpClient, withInterceptors, HttpInterceptorFn, HttpClient } from '@angular/common/http';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
  provideRouter,
  withHashLocation,
  withDisabledInitialNavigation,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { appRoutes } from '@brc/app.routes';
import { GeaTranslateHttpLoader, provideGea } from '@gea/digital-ui-lib';
import { provideAngularSvgIcon } from 'angular-svg-icon';

if (environment.production) {
  enableProdMode();
}

const isIframe = window !== window.parent && !window.opener;

// Ensure translation JSON requests are never cached by the browser/CDN.
const I18N_PREFIX = environment.translationBlob + 'i18n/1/insightpartner-bluredcare/';
const noCacheI18nInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.startsWith(I18N_PREFIX)) {
    const url = new URL(req.url);
    url.searchParams.set('_ts', Date.now().toString());
    const headers = req.headers
      .set('Cache-Control', 'no-cache, no-store, must-revalidate')
      .set('Pragma', 'no-cache')
      .set('Expires', '0');
    return next(req.clone({ url: url.toString(), headers }));
  }
  return next(req);
};

export function HttpLoaderFactory(http: HttpClient) {
  return new GeaTranslateHttpLoader(http, environment.translationBlob, ['i18n/1/insightpartner-bluredcare/']);
}

bootstrapApplication(AppComponent, {
  providers: [
    provideGea({
      redirectUri: environment.oAuth.redirectUri,
      postLogoutRedirectUri: environment.oAuth.postLogoutRedirectUri,
      clientId: environment.oAuth.clientId,
      authority: environment.oAuth.authority,
      authorityDomain: environment.oAuth.authorityDomain,
      portalURL: environment.portal?.baseURL ?? '',
      consentApplicationKey: environment.oAuth.consentApplicationKey,
    }),
    provideRouter(
      appRoutes,
      withHashLocation(),
      isIframe ? withDisabledInitialNavigation() : withEnabledBlockingInitialNavigation()
    ),
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(withInterceptors([noCacheI18nInterceptor])),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      fallbackLang: 'en-US',
      lang: Intl.DateTimeFormat().resolvedOptions().locale,
    }),

    provideAngularSvgIcon(),
    importProvidersFrom(AppCommonModule),
    ...APP_PROVIDERS,
  ],
  // eslint-disable-next-line no-console
}).catch((err) => console.debug({ err }));
